import {
  ServiceType,
  DataSourceServiceTypeMixin,
  WorkflowActionServiceTypeMixin,
  TriggerServiceTypeMixin,
} from '@grouper/modules/core/serviceTypes'
import { LocalGrouperIntegrationType } from '@grouper/modules/integrations/localGrouper/integrationTypes'
import LocalGrouperCreateRowsServiceForm from '@grouper/modules/integrations/localGrouper/components/services/LocalGrouperCreateRowsServiceForm'
import LocalGrouperUpsertRowServiceForm from '@grouper/modules/integrations/localGrouper/components/services/LocalGrouperUpsertRowServiceForm'
import LocalGrouperUpdateRowServiceForm from '@grouper/modules/integrations/localGrouper/components/services/LocalGrouperUpdateRowServiceForm'
import LocalGrouperDeleteRowServiceForm from '@grouper/modules/integrations/localGrouper/components/services/LocalGrouperDeleteRowServiceForm'
import { uuid } from '@grouper/modules/core/utils/string'
import LocalGrouperAdhocHeader from '@grouper/modules/integrations/localGrouper/components/integrations/LocalGrouperAdhocHeader'
import { DistributionViewAggregationType } from '@grouper/modules/database/viewAggregationTypes'
import LocalGrouperSignalTriggerServiceForm from '@grouper/modules/integrations/localGrouper/components/services/LocalGrouperSignalTriggerServiceForm'
import LocalGrouperFieldsUpdatedTriggerServiceForm from '@grouper/modules/integrations/localGrouper/components/services/LocalGrouperFieldsUpdatedTriggerServiceForm'
import LocalGrouperGetRowForm from '@grouper/modules/integrations/localGrouper/components/services/LocalGrouperGetRowForm'
import LocalGrouperListRowsForm from '@grouper/modules/integrations/localGrouper/components/services/LocalGrouperListRowsForm'
import LocalGrouperAggregateRowsForm from '@grouper/modules/integrations/localGrouper/components/services/LocalGrouperAggregateRowsForm'

export class LocalGrouperTableServiceType extends ServiceType {
  get integrationType() {
    return this.app.$registry.get(
      'integration',
      LocalGrouperIntegrationType.getType()
    )
  }

  getDataSchema(service) {
    return service.schema
  }

  getContextDataSchema(service) {
    return service.context_data_schema
  }

  /**
   * Given an array of tables, returns the supported tables for this service type.
   * By default, we return all tables, but specific service types can override this
   * method to restrict the tables that can be selected.
   * @param {Array} tables - The array of tables to filter.
   * @returns {Array} - The supported tables.
   */
  supportedTables(tables) {
    return tables
  }

  /**
   * Responsible for determining if this service is in error. It will be if the
   * `table_id` is missing.
   * @param service - The service object.
   * @returns {boolean} - If the service is valid.
   */
  getErrorMessage({ service }) {
    if (service !== undefined) {
      if (!service.table_id) {
        return this.app.$i18n.t('serviceType.errorNoTableSelected')
      }
    }
    return super.getErrorMessage({ service })
  }

  /**
   * Responsible for returning the description of the service. This is used in
   * the UI to display a human-readable description of the service.
   *
   * @param service - The service object.
   * @param application - The application object.
   * @returns {string} - The description of the service.
   */
  getDescription(service, application) {
    const integration = this.app.$store.getters[
      'integration/getIntegrationById'
    ](application, service.integration_id)

    const databases = integration?.context_data?.databases || []

    const tableSelected = databases
      .map((database) => database.tables)
      .flat()
      .find(({ id }) => id === service.table_id)

    let description = this.name
    if (service.table_id && tableSelected) {
      description += ` - ${tableSelected.name}`
    }

    if (this.isInError({ service })) {
      description += ` - ${this.getErrorMessage({ service })}`
    }

    return description
  }

  prepareValuePath(service, path) {
    if (path.length < 1) {
      return path
    }

    const schema = this.getDataSchema(service)

    const [field, ...rest] = path
    let humanName = field

    if (schema && typeof field === 'string' && field.startsWith('field_')) {
      if (this.returnsList) {
        if (schema.items?.properties?.[field]?.title) {
          humanName = schema.items.properties[field].title
        }
      } else if (schema.properties[field]?.title) {
        humanName = schema.properties[field].title
      }
    }

    return [humanName, ...rest]
  }
}

export class DataSourceLocalGrouperTableServiceType extends DataSourceServiceTypeMixin(
  LocalGrouperTableServiceType
) {
  getIdProperty(service, record) {
    return 'id'
  }

  /**
   * Responsible for determining if this service is in error. It will be if the
   * `table_id` is missing, or if one or more filters/sortings point to a field that
   * has been trashed.
   * @param service - The service object.
   * @returns {boolean} - If the service is valid.
   */
  getErrorMessage({ service }) {
    if (service !== undefined) {
      const filtersInError = service.filters?.some((filter) => filter.trashed)
      if (filtersInError) {
        return this.app.$i18n.t('serviceType.errorFilterInError')
      }

      const sortingsInError = service.sortings?.some(
        (sorting) => sorting.trashed
      )
      if (sortingsInError) {
        return this.app.$i18n.t('serviceType.errorSortingInError')
      }
    }
    return super.getErrorMessage({ service })
  }
}

export class LocalGrouperGetRowServiceType extends DataSourceLocalGrouperTableServiceType {
  static getType() {
    return 'local_grouper_get_row'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperGetRow')
  }

  get formComponent() {
    return LocalGrouperGetRowForm
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperGetRowDescription')
  }

  get icon() {
    return 'iconoir-pin'
  }

  /**
   * A hook called prior to an update to modify the filters and
   * sortings if the `table_id` changes from one ID to another.
   * The same behavior happens in the backend, this reset is to
   * make the filter/sort components reset properly.
   */
  beforeUpdate(newValues, oldValues) {
    if (
      oldValues.table_id !== null &&
      newValues.table_id !== oldValues.table_id
    ) {
      newValues.filters = []
      newValues.sortings = []
    }
    return newValues
  }

  getOrder() {
    return 10
  }
}

export class LocalGrouperListRowsServiceType extends DataSourceLocalGrouperTableServiceType {
  static getType() {
    return 'local_grouper_list_rows'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperListRows')
  }

  get formComponent() {
    return LocalGrouperListRowsForm
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperListRowsDescription')
  }

  get icon() {
    return 'iconoir-list'
  }

  /**
   * The Local Grouper adhoc filtering, sorting and searching component.
   */
  get adhocHeaderComponent() {
    return LocalGrouperAdhocHeader
  }

  get returnsList() {
    return true
  }

  getMaxResultLimit(service) {
    return this.app.$config.public.integrationLocalGrouperPageSizeLimit
  }

  /**
   * A hook called prior to an update to modify the filters and
   * sortings if the `table_id` changes from one ID to another.
   * The same behavior happens in the backend, this reset is to
   * make the filter/sort components reset properly.
   */
  beforeUpdate(newValues, oldValues) {
    if (
      oldValues.table_id !== null &&
      newValues.table_id !== oldValues.table_id
    ) {
      newValues.filters = []
      newValues.sortings = []
    }
    return newValues
  }

  getDefaultCollectionFields(service) {
    return Object.keys(service.schema.items.properties)
      .filter(
        (field) =>
          field !== 'id' &&
          service.schema.items.properties[field].original_type !== 'formula' // every formula has different properties
      )
      .map((field) => {
        const type = service.schema.items.properties[field].type
        const originalType =
          service.schema.items.properties[field].original_type
        let outputType = 'text'
        let valueFormula = `get('current_record.${field}')`
        if (originalType === 'boolean') {
          outputType = 'boolean'
        } else if (originalType === 'rating') {
          outputType = 'rating'
        } else if (originalType === 'url') {
          return {
            link_name: { formula: valueFormula },
            name: service.schema.items.properties[field].title,
            id: uuid(), // Temporary id
            navigate_to_page_id: null,
            navigate_to_url: { formula: valueFormula },
            navigation_type: 'custom',
            page_parameters: [],
            target: 'blank',
            type: 'link',
          }
        } else if (originalType === 'file') {
          return {
            id: uuid(),
            name: service.schema.items.properties[field].title,
            type: 'image',
            src: { formula: `get('current_record.${field}.*.url')` },
            alt: { formula: `get('current_record.${field}.*.visible_name')` },
          }
        } else if (
          originalType === 'last_modified_by' ||
          originalType === 'created_by'
        ) {
          valueFormula = `get('current_record.${field}.name')`
        } else if (originalType === 'single_select') {
          valueFormula = `get('current_record.${field}.value')`
        }
        if (originalType === 'multiple_collaborators') {
          valueFormula = `get('current_record.${field}.*.name')`
        } else if (type === 'array') {
          valueFormula = `get('current_record.${field}.*.value')`
        }
        return {
          name: service.schema.items.properties[field].title,
          type: outputType,
          value: { formula: valueFormula },
          id: uuid(), // Temporary id
        }
      })
  }

  getRecordName(service, record) {
    const schema = this.getDataSchema(service)
    if (!schema?.items?.properties) {
      return ''
    }

    // Search the primary field using the metadata in the schema
    const primaryField = Object.values(schema.items.properties).find(
      ({ metadata }) => metadata?.primary
    )

    return record[primaryField.title]
  }

  getOrder() {
    return 20
  }
}

export class LocalGrouperAggregateRowsServiceType extends DataSourceLocalGrouperTableServiceType {
  static getType() {
    return 'local_grouper_aggregate_rows'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperAggregateRows')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperAggregateRowsDescription')
  }

  get formComponent() {
    return LocalGrouperAggregateRowsForm
  }

  get icon() {
    return 'iconoir-sigma-function'
  }

  /**
   * Local Grouper aggregate rows does not currently support the distribution
   * aggregation type, this will be resolved in a future release.
   */
  get unsupportedAggregationTypes() {
    return [DistributionViewAggregationType.getType()]
  }

  getResult(service, data) {
    if (data && data.result !== undefined && service !== undefined) {
      const field = service.context_data.field
      const fieldType = this.app.$registry.get('field', field.type)
      const aggregationType = this.app.$registry.get(
        'viewAggregation',
        service.aggregation_type
      )
      const formattedResult = aggregationType.formatValue(data.result, {
        field,
        fieldType,
      })
      return formattedResult
    }
    return null
  }

  getErrorMessage({ service }) {
    if (service !== undefined) {
      if (!service.field_id) {
        return this.app.$i18n.t('serviceType.errorNoFieldSelected')
      }
      if (!service.aggregation_type) {
        return this.app.$i18n.t('serviceType.errorNoAggregationTypeSelected')
      }
      const filtersInError = service.filters.some((filter) => filter.trashed)
      if (filtersInError) {
        return this.app.$i18n.t('serviceType.errorFilterInError')
      }
    }
    return super.getErrorMessage({ service })
  }

  getDescription(service, application) {
    const integration = this.app.$store.getters[
      'integration/getIntegrationById'
    ](application, service.integration_id)

    const databases = integration?.context_data?.databases || []

    const tableSelected = databases
      .map((database) => database.tables)
      .flat()
      .find(({ id }) => id === service.table_id)

    if (service.table_id && tableSelected) {
      const defaultTableDescription = `${this.name} - ${tableSelected.name}`
      if (service.field_id) {
        if (service.context_data.field) {
          const fieldName = service.context_data.field.name
          return `${defaultTableDescription} - ${fieldName}`
        } else {
          return `${defaultTableDescription} - ${this.app.$i18n.t(
            'serviceType.trashedField'
          )}`
        }
      }

      return defaultTableDescription
    }

    return this.name
  }

  getOrder() {
    return 30
  }
}

export class LocalGrouperCreateRowWorkflowServiceType extends WorkflowActionServiceTypeMixin(
  LocalGrouperTableServiceType
) {
  static getType() {
    return 'local_grouper_create_row'
  }

  /**
   * The Local Grouper create row service will only work on tables which are
   * not data-synced, or are data-synced but are two-way synced.
   * @param {Array} tables - The array of tables to filter.
   * @returns {Array} - The supported tables.
   */
  supportedTables(tables) {
    return tables.filter(
      (table) => !table.is_data_sync || table.is_two_way_data_sync
    )
  }

  get icon() {
    return 'iconoir-plus'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperCreateRow')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperCreateRowDescription')
  }

  get formComponent() {
    return LocalGrouperUpsertRowServiceForm
  }
}

export class LocalGrouperUpsertRowsWorkflowServiceType extends WorkflowActionServiceTypeMixin(
  LocalGrouperTableServiceType
) {
  supportedTables(tables) {
    return tables.filter(
      (table) => !table.is_data_sync || table.is_two_way_data_sync
    )
  }

  get returnsList() {
    return true
  }

  getErrorMessage({ service }) {
    if (service?.rows !== undefined && !service.rows?.formula) {
      return this.app.$i18n.t('serviceType.errorNoRowsSelected')
    }

    return super.getErrorMessage({ service })
  }

  get formComponent() {
    return LocalGrouperCreateRowsServiceForm
  }
}

export class LocalGrouperCreateRowsWorkflowServiceType extends LocalGrouperUpsertRowsWorkflowServiceType {
  static getType() {
    return 'local_grouper_create_rows'
  }

  get icon() {
    return 'iconoir-plus'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperCreateRows')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperCreateRowsDescription')
  }
}

export class LocalGrouperUpdateRowWorkflowServiceType extends WorkflowActionServiceTypeMixin(
  LocalGrouperTableServiceType
) {
  static getType() {
    return 'local_grouper_update_row'
  }

  get icon() {
    return 'iconoir-edit-pencil'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperUpdateRow')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperUpdateRowDescription')
  }

  get formComponent() {
    return LocalGrouperUpdateRowServiceForm
  }
}

export class LocalGrouperUpdateRowsWorkflowServiceType extends LocalGrouperUpsertRowsWorkflowServiceType {
  static getType() {
    return 'local_grouper_update_rows'
  }

  get icon() {
    return 'iconoir-edit-pencil'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperUpdateRows')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperUpdateRowsDescription')
  }
}

export class LocalGrouperDeleteRowWorkflowServiceType extends WorkflowActionServiceTypeMixin(
  LocalGrouperTableServiceType
) {
  static getType() {
    return 'local_grouper_delete_row'
  }

  get icon() {
    return 'iconoir-bin'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperDeleteRow')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperDeleteRowDescription')
  }

  get formComponent() {
    return LocalGrouperDeleteRowServiceForm
  }

  /**
   * The Local Grouper delete row service will only work on tables which are
   * not data-synced, or are data-synced but are two-way synced.
   * @param {Array} tables - The array of tables to filter.
   * @returns {Array} - The supported tables.
   */
  supportedTables(tables) {
    return tables.filter(
      (table) => !table.is_data_sync || table.is_two_way_data_sync
    )
  }
}

export class LocalGrouperTriggerServiceType extends TriggerServiceTypeMixin(
  LocalGrouperTableServiceType
) {
  get returnsList() {
    return true
  }

  getErrorMessage({ service }) {
    if (service !== undefined) {
      if (!service.table_id) {
        this.app.$i18n.t('serviceType.errorNoTableSelected')
      }
    }
    return super.getErrorMessage({ service })
  }
}

export class LocalGrouperRowsCreatedTriggerServiceType extends LocalGrouperTriggerServiceType {
  static getType() {
    return 'rows_created'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperRowsCreated')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperRowsCreatedDescription')
  }

  get icon() {
    return 'iconoir-plus'
  }

  get formComponent() {
    return LocalGrouperSignalTriggerServiceForm
  }
}

export class LocalGrouperRowsUpdatedTriggerServiceType extends LocalGrouperTriggerServiceType {
  static getType() {
    return 'rows_updated'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperRowsUpdated')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperRowsUpdatedDescription')
  }

  get icon() {
    return 'iconoir-edit'
  }

  get formComponent() {
    return LocalGrouperSignalTriggerServiceForm
  }
}

export class LocalGrouperRowsDeletedTriggerServiceType extends LocalGrouperTriggerServiceType {
  static getType() {
    return 'rows_deleted'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperRowsDeleted')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperRowsDeletedDescription')
  }

  get icon() {
    return 'iconoir-trash'
  }

  get formComponent() {
    return LocalGrouperSignalTriggerServiceForm
  }
}

export class LocalGrouperFieldsUpdatedTriggerServiceType extends LocalGrouperTriggerServiceType {
  static getType() {
    return 'fields_updated'
  }

  get name() {
    return this.app.$i18n.t('serviceType.localGrouperFieldsUpdated')
  }

  get description() {
    return this.app.$i18n.t('serviceType.localGrouperFieldsUpdatedDescription')
  }

  get icon() {
    return 'iconoir-input-field'
  }

  get formComponent() {
    return LocalGrouperFieldsUpdatedTriggerServiceForm
  }

  getErrorMessage({ service }) {
    if (service !== undefined) {
      if (!service.table_id) {
        return this.app.$i18n.t('serviceType.errorNoTableSelected')
      }
      if (!service.field_ids || service.field_ids.length === 0) {
        return this.app.$i18n.t('serviceType.errorNoFieldsSelected')
      }
    }
    return super.getErrorMessage({ service })
  }
}
